import {
    CalendarTodayOutlined,
    MoreVertOutlined as MoreVertOutlinedIcon,
    TaskAltOutlined as TaskAltOutlinedIcon,
} from 'components/icons';
import { cn } from 'utils/cn';
import Checkmark from 'assets/images/icons/CheckmarkIcon.svg?react';
import StatusChip, { ChipType } from 'components/@extended/StatusChip';
import { DraftChip } from 'components/drafts/DraftChip';
import { DraftRowMenu } from 'components/drafts/DraftRecordActions';
import { useWithDrafts } from 'components/drafts/useWithDrafts';
import ListCreateButton from 'components/buttons/ListCreateButton';
import Loader from 'components/Loader';
import ConfirmationModal from 'components/modals/confirmation/ConfirmationModal';
import { useModal } from 'components/modals/useModal';
import KebabMenu, { MenuItem as KebabMenuItem } from 'components/navigation/KebabMenu';
import type { DataTableColumnDef } from 'components/tables/data-table';
import { getFilterOptionsFromClassifier } from 'components/tables/list-table/filterUtil.ts';
import { EmployeeNameLink } from 'components/record-chip/RecordLink';
import { FilterConfig } from 'components/tables/list-table/ListTableTypes';
import ListTableWithFilters from 'components/tables/list-table/ListTableWithFilters';
import { Tooltip } from 'components/ui/tooltip';
import LinkWithOrigin from 'components/util/LinkWithOrigin';
import NMoreChip from 'components/util/NMoreChip.tsx';
import { format } from 'date-fns';
import useAuth from 'hooks/useAuth.ts';
import { useEffectiveDateFormat } from 'hooks/useEffectiveDateFormat';
import { getLinks } from 'pages/matters/util.tsx';
import MatterIndicators from 'components/matters/MatterIndicators';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import { useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useLazyGetMinContactsListWithFiltersQuery } from 'store/api/contacts.ts';
import { useLazyGetCorrespondenceMinListQuery } from 'store/api/correspondence.ts';
import { useLazyGetEmployeesListWithFiltersQuery } from 'store/api/employees.ts';
import { useLazyGetMattersListQuery } from 'store/api/matters.ts';
import { useDeleteTaskMutation, useLazyGetTasksWithFiltersQuery, useSwitchTasksStatusMutation } from 'store/api/tasks';
import { FeatureFlag } from 'types/features';
import { NamedEntityMinDto } from 'types/other';
import { getPriorityStatusChipType, getTaskUrl, TaskListItem, TaskStatusType } from 'types/tasks';
import { errorAlert } from 'utils/alert';
import { formatCalendarEventForDisplay, isDateEarlierThanToday } from 'utils/date.ts';
import { getClassifierText, t } from 'utils/getTranslations';
import { renderMatterIndicators } from 'utils/matterOptionRenderer';
import { partyNameString } from 'utils/partyNameUtil';
import { useHasPermissions } from 'utils/permissionUtil.ts';
import { sortByField } from 'utils/sort.ts';
import { useClassifiers } from 'utils/useClassifiers';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';
import { useNavigateWithReturnState } from 'utils/useNavigateWithReturnState';

interface Props {
    filterConfig?: FilterConfig;
    initialSearchParams?: string;
    canRememberFilters?: boolean;
    matterId?: string;
    clientPartyId?: string;
    correspondenceId?: string;
    debriefingId?: string;
}

const visibleEmployeesCount = 2;

// `entity-link` hands the cell's link colour and wrapping to the table's own rule.
const LINK_CLASS = 'entity-link text-fg hover:text-link w-fit no-underline hover:underline';

// Wraps, then ends in an ellipsis on its second line, as the case list's name does.
const TASK_NAME_CLASS = 'text-body1 text-fg line-clamp-2 font-semibold';

const openTaskStatusTypes = [TaskStatusType.IN_PROGRESS];

const TasksTable = ({
    filterConfig,
    initialSearchParams,
    canRememberFilters = true,
    matterId,
    clientPartyId,
    correspondenceId,
    debriefingId,
}: Props): JSX.Element => {
    const effectiveFormat = useEffectiveDateFormat();
    const isPartyNumberUsageEnabled = useIsFeatureEnabled(FeatureFlag.PARTY_NUMBER_USAGE);
    const isCorrespondenceEnabled = useIsFeatureEnabled(FeatureFlag.CORRESPONDENCE_MODULE);
    const navigateWithReturn = useNavigateWithReturnState();
    const isNewDesign = useIsNewDesign();

    // ONE route for both the header button and the in-place empty-state CTA: on a case,
    // correspondence, contact or debriefing tab the new task must stay associated with it.
    const createTaskUrl = matterId
        ? `/matter/${matterId}/task/new`
        : correspondenceId
          ? `/correspondence/${correspondenceId}/task/new`
          : clientPartyId
            ? `/contact/${clientPartyId}/task/new`
            : debriefingId
              ? `/debriefing/${debriefingId}/task/new`
              : '/task/new';
    const isGlobalList = !matterId && !correspondenceId && !clientPartyId && !debriefingId;
    const { openModal, closeModal } = useModal();
    const { user } = useAuth();
    const currentUserId = user?.id;
    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({
        codes: ['TASK_PRIORITY_TYPE', 'TASK_STATUS_TYPE', 'TASK_TYPE', 'CORRESPONDENCE_TYPE'],
    });
    const hasTaskCreatedByMePermission = useHasPermissions(['READ.TASKS.CREATED_BY_ME']);
    const hasDeleteTaskPermission = useHasPermissions(['DELETE.TASKS.ALL', 'DELETE.TASKS.CREATED_BY_ME']);

    const [fetchTasks, { data, isFetching }] = useLazyGetTasksWithFiltersQuery();
    const fetchData = useWithDrafts(fetchTasks);
    const [deleteTask, { isLoading: deleteTaskLoading }] = useDeleteTaskMutation();
    const [switchTaskStatus, { isLoading: isSwitchingStatus }] = useSwitchTasksStatusMutation();

    // The API layer toasts a failed switch. The new design shows the switch before the answer.
    const handleSwitchTaskStatus = useCallback(
        (id: number, taskStatusTypeCode: string) => {
            const nextStatus =
                taskStatusTypeCode === TaskStatusType.DONE ? TaskStatusType.IN_PROGRESS : TaskStatusType.DONE;
            void switchTaskStatus({ id: `${id}`, nextStatus: isNewDesign ? nextStatus : undefined })
                .unwrap()
                .catch((e: unknown) => console.error('onSubmit error: ', e));
        },
        [isNewDesign, switchTaskStatus]
    );

    const getAssignedToEmployees = useCallback(
        (employees: NamedEntityMinDto[]) => {
            const sortedEmployees = employees
                .toSorted((a, b) => {
                    if (a.id === currentUserId) return -1;
                    if (b.id === currentUserId) return 1;
                    return a.name.localeCompare(b.name);
                })
                .map((employee) =>
                    employee.id === currentUserId
                        ? { ...employee, name: t('tasks.table.you'), firstName: undefined, lastName: undefined }
                        : employee
                );

            const visibleEmployees = sortedEmployees.slice(0, visibleEmployeesCount);
            const hiddenEmployees = sortedEmployees.slice(visibleEmployeesCount);

            return (
                <div className="flex flex-col justify-start">
                    {visibleEmployees.map((employee) => (
                        <EmployeeNameLink
                            className={LINK_CLASS}
                            key={employee.id}
                            to={`/employee/${employee.id}`}
                            entity={employee}
                        />
                    ))}
                    {hiddenEmployees.length > 0 && (
                        <div>
                            <Tooltip
                                content={
                                    <div className="flex flex-col">
                                        {getLinks('employee', hiddenEmployees, { separator: null })}
                                    </div>
                                }
                            >
                                <NMoreChip count={hiddenEmployees.length} />
                            </Tooltip>
                        </div>
                    )}
                </div>
            );
        },
        [currentUserId]
    );

    const getPriorityStatusChip = useCallback(
        (priorityTypeCode: string) => {
            // A draft may have none yet.
            return priorityTypeCode ? (
                <StatusChip
                    type={getPriorityStatusChipType(priorityTypeCode)}
                    label={getClassifierText(classifiers['TASK_PRIORITY_TYPE'], priorityTypeCode)}
                />
            ) : null;
        },
        [classifiers]
    );

    const deleteTaskHandler = useCallback(
        async (taskId: number) => {
            try {
                if (taskId && !isNaN(Number(taskId))) {
                    openModal(
                        <ConfirmationModal
                            onCancel={closeModal}
                            onConfirm={() => {
                                void deleteTask({ id: `${taskId}` })
                                    .unwrap()
                                    .catch((e) => {
                                        console.error(e);
                                        errorAlert(t('forms.error-submitting-the-form'));
                                    })
                                    .finally(() => closeModal());
                            }}
                            content={<p className="text-body1">{t('tasks.alerts.delete-confirmation-text')}</p>}
                        />
                    );
                }
            } catch (e) {
                console.error('onSubmit error: ', e);
                errorAlert(t('forms.error-submitting-the-form'));
            }
        },
        [closeModal, openModal, deleteTask]
    );

    const getTaskKebabMenu = useCallback(
        (taskId: number, taskStatusTypeCode: string): KebabMenuItem[] => {
            const btnLabel =
                taskStatusTypeCode == TaskStatusType.IN_PROGRESS ? 'tasks.table.mark-as-done' : 'tasks.table.open-task';
            const menuItems: KebabMenuItem[] = [
                {
                    handler: () => {
                        navigateWithReturn(`/task/new/${taskId}`);
                    },
                    title: t('tasks.table.duplicate-task'),
                },
            ];

            if (hasDeleteTaskPermission) {
                menuItems.push({
                    handler: () => {
                        void deleteTaskHandler(taskId);
                    },
                    title: t('tasks.table.delete-task'),
                    variant: 'destructive',
                });
            }

            menuItems.push({
                handler: () => {
                    handleSwitchTaskStatus(taskId, taskStatusTypeCode);
                },
                title: t(btnLabel),
                // The endpoint toggles: in the new design, a second press while one runs would switch the row back.
                disabled: isNewDesign && isSwitchingStatus,
                // Classic's amber; the new design takes the menu's own ink, which reads on its light menu.
                className:
                    'text-secondary focus:text-secondary new:text-popover-foreground new:focus:text-accent-foreground',
            });

            return menuItems;
        },
        [
            deleteTaskHandler,
            handleSwitchTaskStatus,
            navigateWithReturn,
            hasDeleteTaskPermission,
            isNewDesign,
            isSwitchingStatus,
        ]
    );

    const columns = useMemo<DataTableColumnDef<TaskListItem>[]>(
        () => [
            {
                header: t('tasks.table.name-contact-case'),
                id: 'task',
                enableSorting: false,
                meta: {
                    // Weights measured at ~1680 (the primary target). Name/contact/case is the one
                    // content-heavy column (~2.8x); priority/status/is-automatic/kebab are
                    // content-locked (constant px at every width) so they are fixedPx here.
                    widthWeight: 2.8,
                    minPx: 180,
                },
                cell: ({ cell }) => {
                    const { name, matters, clientParties: unsortedClientParties, taskTypeCodes } = cell.row.original;

                    const clientParties = sortByField(unsortedClientParties, 'name');
                    const moreMatters = (
                        <Tooltip
                            content={
                                <div className="flex flex-col">
                                    {matters.slice(1).map((matter) => (
                                        <span key={matter.id}>
                                            {getLinks('matter', [
                                                {
                                                    id: matter.id,
                                                    name: matter.fullName,
                                                    prefix: <MatterIndicators isConfidential={matter.isConfidential} />,
                                                    suffix: matter.clientParty ? (
                                                        <span>({getLinks('contact', [matter.clientParty])})</span>
                                                    ) : null,
                                                },
                                            ])}
                                        </span>
                                    ))}
                                </div>
                            }
                        >
                            <NMoreChip count={matters.length - 1} />
                        </Tooltip>
                    );

                    const firstMatter = matters.length > 0 ? matters.at(0) : undefined;
                    const firstClientParty = clientParties.length > 0 ? clientParties.at(0) : undefined;
                    const types = (taskTypeCodes || [])
                        .map((code) => getClassifierText(classifiers['TASK_TYPE'], code))
                        .filter((item) => !!item)
                        .join(', ');
                    // A draft may be saved before it is named.
                    const shownName = name || t('new-entity.task');
                    const title = types ? `${types}, ${shownName}` : shownName;
                    const titleUrl = getTaskUrl(cell.row.original);

                    return (
                        <div className="flex min-w-0 flex-col gap-2 overflow-hidden">
                            {titleUrl === undefined ? (
                                <span className={TASK_NAME_CLASS} title={title}>
                                    {title}
                                </span>
                            ) : (
                                <LinkWithOrigin to={titleUrl} title={title} className={cn(LINK_CLASS, TASK_NAME_CLASS)}>
                                    {title}
                                </LinkWithOrigin>
                            )}
                            {firstMatter ? (
                                <div className="flex min-w-0 flex-col overflow-hidden">
                                    <span className="text-body1 truncate">
                                        {getLinks('matter', [
                                            {
                                                id: firstMatter.id,
                                                name: firstMatter.fullName,
                                                // The new design puts the chip on the cell's bottom line.
                                                prefix: isNewDesign ? undefined : (
                                                    <MatterIndicators isConfidential={firstMatter.isConfidential} />
                                                ),
                                                suffix: firstMatter.clientParty ? (
                                                    <span>
                                                        (
                                                        {getLinks('contact', [
                                                            {
                                                                id: firstMatter.clientParty.id,
                                                                name: partyNameString(firstMatter.clientParty, {
                                                                    showPartyNumber: true,
                                                                    isPartyNumberUsageEnabled,
                                                                }),
                                                            },
                                                        ])}
                                                        )
                                                    </span>
                                                ) : null,
                                            },
                                        ])}
                                    </span>
                                    {isNewDesign ? (
                                        firstMatter.isConfidential || matters.length > 1 ? (
                                            <div className="flex flex-wrap items-center gap-1">
                                                <MatterIndicators isConfidential={firstMatter.isConfidential} />
                                                {matters.length > 1 ? moreMatters : null}
                                            </div>
                                        ) : null
                                    ) : matters.length > 1 ? (
                                        <div>{moreMatters}</div>
                                    ) : null}
                                </div>
                            ) : firstClientParty ? (
                                <div className="flex min-w-0 flex-col overflow-hidden">
                                    <span className="text-body1 truncate">
                                        {getLinks('contact', [firstClientParty])}
                                    </span>
                                    {clientParties.length > 1 ? (
                                        <div>
                                            <Tooltip
                                                content={
                                                    <div className="flex flex-col">
                                                        {clientParties.slice(1).map((client) => (
                                                            <span key={client.id}>{getLinks('contact', [client])}</span>
                                                        ))}
                                                    </div>
                                                }
                                            >
                                                <NMoreChip count={clientParties.length - 1} />
                                            </Tooltip>
                                        </div>
                                    ) : null}
                                </div>
                            ) : null}
                        </div>
                    );
                },
            },
            {
                header: t('tasks.table.assigned-by'),
                accessorKey: 'assignedByEmployees',
                enableSorting: false,
                meta: {
                    widthWeight: 1.25,
                    minPx: 100,
                },
                cell: ({ getValue }) => {
                    return getAssignedToEmployees(getValue<NamedEntityMinDto[]>() || []);
                },
            },
            {
                header: t('tasks.table.assigned-to'),
                accessorKey: 'assignedToEmployees',
                enableSorting: false,
                meta: {
                    widthWeight: 1.3,
                    minPx: 100,
                },
                cell: ({ getValue }) => {
                    return getAssignedToEmployees(getValue<NamedEntityMinDto[]>() || []);
                },
            },
            {
                header: t('tasks.table.deadline'),
                id: 'deadlineDate',
                accessorKey: 'deadlineDate',
                meta: {
                    sortAccessor: 'deadlineDate',
                    nullHandling: 'nullsLast',
                    widthWeight: 1,
                    minPx: 95,
                },
                cell: ({ cell, getValue }) => {
                    const value = getValue<string | undefined>();
                    const { taskStatusTypeCode } = cell.row.original;
                    const isDone = taskStatusTypeCode === TaskStatusType.DONE;
                    const isOverdue = !isDone && !!value && isDateEarlierThanToday(value);
                    const date = value ? format(new Date(value), effectiveFormat) : '';
                    return <p className={cn('text-body1', isOverdue && 'text-fg-error')}>{date}</p>;
                },
            },
            {
                header: t('tasks.table.calendar-event'),
                id: 'calendarEventDisplay',
                accessorKey: 'calendarEventDisplay',
                meta: {
                    sortAccessor: 'taskEvent.dateStart',
                    nullHandling: 'nullsLast',
                    widthWeight: 1.15,
                    minPx: 100,
                },
                cell: ({ cell, getValue }) => {
                    const { isAllDayEvent, calendarEventStartDate, taskStatusTypeCode } = cell.row.original;
                    const { text, isAllDay } = formatCalendarEventForDisplay(
                        getValue<string | undefined>(),
                        isAllDayEvent,
                        effectiveFormat
                    );
                    const isDone = taskStatusTypeCode === TaskStatusType.DONE;
                    const isPastEvent =
                        !isDone && !!calendarEventStartDate && isDateEarlierThanToday(calendarEventStartDate);
                    return (
                        <div className="flex items-center gap-1">
                            {isAllDay && (
                                <Tooltip content={t('tasks.table.all-day-event')}>
                                    <CalendarTodayOutlined className={cn('text-[1rem]', isPastEvent && 'text-error')} />
                                </Tooltip>
                            )}
                            <p className={cn('text-body1', isPastEvent && 'text-fg-error')}>{text}</p>
                        </div>
                    );
                },
            },
            {
                header: t('tasks.table.created'),
                id: 'createdAt',
                accessorKey: 'createdAt',
                meta: {
                    sortAccessor: 'createdAt',
                    nullHandling: 'nullsLast',
                    widthWeight: 1,
                    minPx: 95,
                },
                cell: ({ getValue }) => {
                    const value = getValue<string | undefined>();
                    const date = value ? format(new Date(value), effectiveFormat) : '';
                    return <p className="text-body1">{date}</p>;
                },
            },
            {
                header: t('tasks.table.official-ref-number'),
                accessorKey: 'officialReference',
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    minPx: 95,
                },
                cell: ({ cell }) => {
                    const { isOfficial, officialReference } = cell.row.original;
                    return (
                        <div className="flex flex-col">
                            <p className="text-body1">
                                {isOfficial ? <FormattedMessage id="correspondence.official" /> : '-'}
                            </p>
                            <p className="text-body1">{officialReference}</p>
                        </div>
                    );
                },
            },
            {
                header: t('tasks.table.priority'),
                accessorKey: 'taskPriorityTypeCode',
                id: 'taskPriorityTypeCode',
                meta: {
                    sortAccessor: 'taskPriorityTypeCode',
                    // Content-locked: constant px at every width.
                    fixedPx: 95,
                },
                cell: ({ getValue }) => getPriorityStatusChip(getValue<string>()),
            },
            {
                header: t('tasks.table.status'),
                accessorKey: 'taskStatusTypeCode',
                enableSorting: false,
                meta: {
                    fixedPx: 113,
                },
                cell: ({ getValue, row }) => {
                    const value = getValue();
                    let statusChipType: ChipType;
                    switch (value) {
                        case TaskStatusType.DONE:
                            statusChipType = 'neutral';
                            break;
                        case TaskStatusType.IN_PROGRESS:
                            statusChipType = 'success';
                            break;
                    }
                    const status = (
                        <StatusChip
                            className="mt-1 cursor-pointer"
                            type={statusChipType}
                            label={getClassifierText(classifiers['TASK_STATUS_TYPE'], value)}
                        />
                    );
                    return row.original.isDraft ? (
                        <div className="flex flex-col items-start gap-1">
                            <DraftChip className="mt-1" />
                            {status}
                        </div>
                    ) : (
                        status
                    );
                },
            },
            {
                header: t('tasks.table.is-automatic'),
                accessorKey: 'isAutomatic',
                enableSorting: false,
                meta: {
                    fixedPx: 96,
                },
                cell: ({ getValue }) => {
                    return getValue() ? <Checkmark className="text-success" width={16} height={16} /> : null;
                },
            },
            {
                header: t('tasks.table.task-type'),
                accessorKey: 'taskTypeCodes',
                enableSorting: false,
                meta: {
                    widthWeight: 1.2,
                    minPx: 100,
                },
                cell: ({ getValue }) => {
                    const value = getValue();
                    const taskTypes = Array.isArray(value)
                        ? value
                              .map((el) => getClassifierText(classifiers['TASK_TYPE'], el))
                              .sort((a, b) => a.localeCompare(b))
                              .join(', ')
                        : '';
                    return <p className="text-body1 text-fg">{taskTypes}</p>;
                },
            },
            {
                id: 'button',
                enableSorting: false,
                meta: {
                    textAlign: 'right',
                    // Fixed column (single kebab-menu button) — excluded from the weighted flex.
                    fixedPx: 76,
                },
                cell: ({ cell }) => {
                    const { id, taskStatusTypeCode, isAutomatic, isDraft } = cell.row.original;
                    // Every action needs a task row (the id is null on an automatic task).
                    if (isAutomatic) return null;
                    if (isDraft) return <DraftRowMenu draft={{ type: 'task', id }} />;
                    return (
                        <KebabMenu
                            menuItems={getTaskKebabMenu(id, taskStatusTypeCode)}
                            icon={<MoreVertOutlinedIcon fontSize="medium" />}
                            className="h-full"
                        />
                    );
                },
            },
        ],
        [
            classifiers,
            isPartyNumberUsageEnabled,
            getAssignedToEmployees,
            getPriorityStatusChip,
            getTaskKebabMenu,
            effectiveFormat,
            isNewDesign,
        ]
    );

    const memoizedFilterConfig: FilterConfig = useMemo(
        () => ({
            quickFilters: [
                // Shortcut onto the status multiselect below: the list opens on open tasks only, and
                // this widens it to include done ones. Both controls drive `taskStatusTypeCode` and
                // the filter hook keeps them in step.
                {
                    selector: 'taskStatusTypeCode',
                    multiple: true,
                    filters: [
                        {
                            label: t('tasks.table.show-done-tasks'),
                            argument: TaskStatusType.DONE,
                            tooltip: 'tasks.table.show-done-tasks-tooltip',
                        },
                    ],
                },
                {
                    selector: 'id',
                    comperator: '=istaskassignedtouser=',
                    filters: [
                        {
                            label: t('tasks.table.assigned-to-me'),
                            argument: currentUserId?.toString() ?? '',
                            tooltip: 'tasks.table.tasks-assigned-to-me-tooltip',
                        },
                    ],
                },
                {
                    selector: 'hasDeadline',
                    filters: [
                        {
                            label: t('tasks.table.with-deadline'),
                            argument: 'true',
                        },
                    ],
                },
                {
                    selector: 'taskPriorityTypeCode',
                    filters: [
                        {
                            label: t('tasks.table.high-priority'),
                            argument: 'HIGH',
                        },
                    ],
                },
                {
                    selector: 'deadlineDate',
                    comperator: '=istaskoverdue=',
                    filters: [
                        {
                            label: t('tasks.filter.overdue-tasks'),
                            argument: 'true',
                            tooltip: 'tasks.tooltips.overdue-tasks',
                        },
                    ],
                },
                ...(hasTaskCreatedByMePermission
                    ? [
                          {
                              selector: 'id',
                              comperator: '=istaskcreatedbyme=',
                              filters: [
                                  {
                                      label: t('tasks.filter.created-by-me'),
                                      argument: 'true',
                                  },
                              ],
                          },
                      ]
                    : []),
            ],
            filters: [
                {
                    type: 'text',
                    label: t('tasks.filter.task-name'),
                    group: 'tasks.filter.task-section',
                    selector: 'name',
                },
                {
                    type: 'text',
                    label: t('tasks.filter.task-desc'),
                    group: 'tasks.filter.task-section',
                    selector: 'description',
                },
                {
                    type: 'multiselect',
                    label: t('tasks.filter.task-type'),
                    group: 'tasks.filter.task-section',
                    selector: 'taskTypes.taskTypeCode',
                    options: classifiers ? getFilterOptionsFromClassifier(classifiers?.['TASK_TYPE']) : [],
                },
                {
                    type: 'multiselect',
                    label: t('tasks.filter.task-status'),
                    group: 'tasks.filter.task-section',
                    selector: 'taskStatusTypeCode',
                    options: classifiers ? getFilterOptionsFromClassifier(classifiers?.['TASK_STATUS_TYPE']) : [],
                },
                {
                    type: 'multiselect',
                    label: t('tasks.filter.task-priority'),
                    group: 'tasks.filter.task-section',
                    selector: 'taskPriorityTypeCode',
                    options: classifiers ? getFilterOptionsFromClassifier(classifiers?.['TASK_PRIORITY_TYPE']) : [],
                },
                {
                    type: 'checkboxgroup',
                    group: 'tasks.filter.task-section',
                    selector: 'isOfficial',
                    filters: [
                        {
                            label: t('tasks.filter.official'),
                            argument: 'true',
                        },
                    ],
                },
                {
                    type: 'text',
                    label: t('tasks.filter.official-reference-number'),
                    group: 'tasks.filter.task-section',
                    selector: 'officialReference',
                },
                {
                    type: 'checkboxgroup',
                    selector: 'id',
                    comperator: '=istaskassignedtouser=',
                    group: 'tasks.filter.colleagues-section',
                    filters: [
                        {
                            label: t('tasks.table.assigned-to-me'),
                            argument: currentUserId?.toString() ?? '',
                            tooltip: 'tasks.table.tasks-assigned-to-me-tooltip',
                        },
                    ],
                },
                {
                    type: 'dynamicmultiselect',
                    group: 'tasks.filter.colleagues-section',
                    label: t('tasks.filter.assigned-to'),
                    comperator: '=istaskassignedtouser=',
                    selector: 'taskEmployees',
                    paginated: {
                        fetch: useLazyGetEmployeesListWithFiltersQuery,
                        searchField: 'fullName',
                        getOptionIdentifier: 'id',
                        getOptionLabel: 'name',
                    },
                },
                {
                    type: 'dynamicmultiselect',
                    group: 'tasks.filter.colleagues-section',
                    label: t('tasks.filter.assigned-by'),
                    comperator: '=istaskassignedbyuser=',
                    selector: 'taskEmployees',
                    paginated: {
                        fetch: useLazyGetEmployeesListWithFiltersQuery,
                        searchField: 'fullName',
                        getOptionIdentifier: 'id',
                        getOptionLabel: 'name',
                    },
                },
                {
                    type: 'dynamicmultiselect',
                    group: 'tasks.filter.colleagues-section',
                    label: t('tasks.filter.created-by'),
                    selector: 'createdByEmployeeId',
                    paginated: {
                        fetch: useLazyGetEmployeesListWithFiltersQuery,
                        searchField: 'fullName',
                        getOptionIdentifier: 'id',
                        getOptionLabel: 'name',
                    },
                },
                {
                    type: 'dynamicmultiselect',
                    subType: 'contact',
                    group: 'tasks.filter.task-connections',
                    label: t('tasks.filter.connected-contact'),
                    selector: 'clientParties.id',
                    paginated: {
                        fetch: useLazyGetMinContactsListWithFiltersQuery,
                        searchField: ['name', 'partyNumber'],
                        getOptionIdentifier: 'id',
                        getOptionLabel: 'name',
                    },
                },
                {
                    type: 'dynamicmultiselect',
                    group: 'tasks.filter.task-connections',
                    label: t('tasks.filter.connected-case'),
                    selector: 'taskMatters.matter.id',
                    getOptionStartAdornment: renderMatterIndicators,
                    paginated: {
                        fetch: useLazyGetMattersListQuery,
                        searchField: ['fullName', 'clientParty.name'],
                        getOptionIdentifier: 'id',
                        getOptionLabel: 'fullName',
                        sort: ['clientParty.name,asc', 'name,asc'],
                        groupByField: 'clientParty.name',
                    },
                },
                ...(isCorrespondenceEnabled
                    ? [
                          {
                              type: 'dynamicmultiselect' as const,
                              group: 'tasks.filter.task-connections',
                              label: t('tasks.filter.connected-correspondence'),
                              selector: 'taskCorrespondence.correspondence.id',
                              paginated: {
                                  fetch: useLazyGetCorrespondenceMinListQuery,
                                  searchField: ['number', 'name', 'fullName'],
                                  getOptionLabel: 'fullName',
                                  getOptionIdentifier: 'id',
                              },
                          },
                      ]
                    : []),
                {
                    type: 'checkboxgroup',
                    group: 'tasks.filter.deadline-section',
                    selector: 'deadlineDate',
                    comperator: '=istaskoverdue=',
                    filters: [
                        {
                            label: t('tasks.filter.overdue-tasks'),
                            argument: 'true',
                            tooltip: 'tasks.tooltips.overdue-tasks',
                        },
                    ],
                },
                {
                    type: 'checkboxgroup',
                    group: 'tasks.filter.deadline-section',
                    selector: 'hasDeadline',
                    filters: [
                        {
                            label: t('tasks.table.with-deadline'),
                            argument: 'true',
                            tooltip: 'tasks.filter.with-deadline-tooltip',
                        },
                    ],
                },
                {
                    type: 'daterange',
                    group: 'tasks.filter.deadline-section',
                    selector: 'deadlineDate',
                    label: t('tasks.filter.deadline-from'),
                    labelTo: t('tasks.filter.deadline-to'),
                    shouldDisplayQuickPick: false,
                },
            ],
            searchBarSelectors: [
                { selector: 'name' },
                { selector: 'clientParties.name' },
                { selector: 'taskEmployees.employee.fullName' },
                { selector: 'taskPriorityTypeCode' },
                { selector: 'taskStatusTypeCode' },
                { selector: 'taskMatters.matter.name' },
                { selector: 'taskMatters.matter.caseNumber' },
                {
                    selector: 'taskTypes.taskTypeCode',
                    comparator: '=classifiertexteq=',
                    arguments: () => ['ENG', 'TASK_TYPE'],
                },
                { selector: 'deadlineDate', comparator: '=datelk=' },
                { selector: 'officialReference' },
                { selector: 'taskEvent.dateStart', comparator: '=offsetdatetimelk=' },
                { selector: 'taskEvent.dateEnd', comparator: '=offsetdatetimelk=' },
            ],
            passiveFilter: '',
        }),
        [classifiers, currentUserId, hasTaskCreatedByMePermission, isCorrespondenceEnabled]
    );

    if (isLoadingClassifiers || deleteTaskLoading) {
        return <Loader />;
    }

    return (
        <ListTableWithFilters<TaskListItem>
            getRowHref={getTaskUrl}
            // Managed v8 engine for the weighted-width layout. The global list gets show/hide, drag to
            // reorder and drag to resize; the case/contact/correspondence/debriefing tabs get none, and
            // their own key, so the global list's saved layout is not replayed onto them.
            columnConfigId={isGlobalList ? 'tasks' : 'tasks-tab'}
            enableColumnPicker={isGlobalList}
            enableColumnReorder={isGlobalList}
            enableColumnResize={isGlobalList}
            columns={columns}
            data={data}
            isLoading={isFetching}
            pluralTextId="tasks.table.tasks"
            singularTextId="tasks.table.task"
            noEntriesId="tasks.table.no-tasks-found"
            noEntriesBodyId="tasks.empty.body"
            noEntriesIcon={TaskAltOutlinedIcon}
            noEntriesAction={
                <ListCreateButton
                    permissions={['CREATE.TASKS']}
                    variant="contained"
                    color="secondary"
                    onClick={() => navigateWithReturn(createTaskUrl)}
                    labelId="tasks.add-task"
                    newLabelId="new-entity.task"
                />
            }
            fetchData={fetchData}
            filterConfig={filterConfig ?? memoizedFilterConfig}
            rowsPerPage={50}
            extraHeaderComponent={
                isGlobalList ? null : (
                    <ListCreateButton
                        permissions={['CREATE.TASKS']}
                        variant="contained"
                        color="secondary"
                        size="small"
                        onClick={() => navigateWithReturn(createTaskUrl)}
                        labelId="tasks.add-task"
                        newLabelId="new-entity.task"
                    />
                )
            }
            initialSearchParams={
                initialSearchParams ??
                `mf=taskStatusTypeCode=in=(${openTaskStatusTypes.join(',')})` +
                    (currentUserId ? `&qf=id=istaskassignedtouser=${currentUserId?.toString() ?? ''}` : '')
            }
            canRememberFilters={canRememberFilters}
        />
    );
};

export default TasksTable;
